
/**
 * Module dependencies.
 */

import { ElementType, MouseEvent } from 'react';
import { Icon } from 'react-components/media';
import { Loading } from '../loading';
import { RouterLink } from '../links/router-link';
import { color } from 'react-components/styles';
import { colorThemes } from './styles';
import { get } from 'lodash';
import { ifProp, switchProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * Icon color themes.
 */

const iconColorThemes = {
  ...colorThemes,
  transparent: {
    backgroundColor: 'transparent',
    color: color('secondary'),
    disabled: {
      color: color('blue200')
    },
    interaction: {
      color: color('blue500')
    }
  }
};

/**
 * `ColorTheme` type.
 */

type ColorTheme = keyof typeof iconColorThemes;

/**
 * Export `Size` type.
 */

export type Size = 'large' | 'medium' | 'small';

/**
 * Export `IconButtonProps` type.
 */

export type IconButtonProps = {
  'aria-label': string;
  as?: string | ElementType;
  colorTheme?: ColorTheme;
  disabled?: boolean;
  href?: string;
  icon: string;
  iconSize: Size | string;
  isSubmitting?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  size?: Size | string;
  to?: any;
  type?: 'button' | 'submit';
};

/**
 * Icon button themes.
 */

const iconButtonThemes = Object.entries(iconColorThemes).reduce((result, [themeName, themeSchema]) => ({
  ...result,
  [themeName]: css`
      background-color: ${get(themeSchema, 'backgroundColor')};
      color: ${get(themeSchema, 'color')};

      ${ifProp('disabled', css`
        background-color: ${get(themeSchema, 'disabled.backgroundColor')};
        color: ${get(themeSchema, 'disabled.color')};
      `)}

      &:focus,
      &:hover {
        background-color: ${get(themeSchema, 'interaction.backgroundColor')};
        color: ${get(themeSchema, 'interaction.color')};
      }
    `
}), {});

/**
 * `Button` styled component.
 */

const Button = styled.button<{ colorTheme?: ColorTheme; disabled?: boolean; size: Size | string }>`
  align-items: center;
  appearance: none;
  border: 0;
  border-radius: ${theme('dimensions.borderRadius')}px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  line-height: 11px;
  outline: none;
  padding: 0;
  position: relative;
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, color;

  ${ifProp('disabled', css`
    cursor: default;
    pointer-events: none;
  `)}

  ${switchProp('size', {
    large: css`
      height: 48px;
      width: 48px;
    `,
    medium: css`
      height: 36px;
      width: 36px;
    `,
    small: css`
      height: 32px;
      width: 32px;
    `
  }, css`
    height: ${props => (props as unknown as { size: string }).size};
    width: ${props => (props as unknown as { size: string }).size};
  `)}

  ${switchProp('colorTheme', iconButtonThemes)}
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading)`
  left: initial;
  top: initial;
  transform: none;
`;

/**
 * Get icon size.
 */

function getIconSize(size: Size | string): string {
  switch (size) {
    case 'large':
      return '32px';

    case 'medium':
      return '20px';

    case 'small':
      return '16px';

    default:
      return size;
  }
}

/**
 * Export `IconButton` component.
 */

export function IconButton(props: IconButtonProps): JSX.Element {
  const {
    as,
    disabled,
    href,
    icon,
    iconSize,
    isSubmitting,
    onClick,
    size = 'medium',
    to,
    type = 'button',
    ...rest
  } = props;

  if (to) {
    return (
      <Button
        {...rest}
        as={RouterLink}
        disabled={disabled}
        size={size}
        to={to}
      >
        {isSubmitting ? (
          <StyledLoading
            active
            relative
          />
        ) : (
          <Icon
            aria-hidden
            icon={icon}
            size={getIconSize(iconSize)}
          />
        )}
      </Button>
    );
  }

  return (
    <Button
      {...as ? { as } : {}}
      {...href ? { href } : {}}
      {...onClick ? { disabled, onClick, type } : {}}
      {...rest}
      aria-disabled={disabled}
      size={size}
    >
      {isSubmitting ? (
        <StyledLoading
          active
          relative
        />
      ) : (
        <Icon
          aria-hidden
          icon={icon}
          size={getIconSize(iconSize)}
        />
      )}
    </Button>
  );
}
